// Mantenimiento M1: QR por activo. El QR lleva un enlace estable de StorSafe con el id del activo (?activo=<id>), sin
// ningún otro dato. La ficha se abre SOLO con sesión iniciada y para usuarios de la empresa del activo: sin sesión, la
// app muestra el login y no consulta nada; con sesión de otra empresa, la RLS no devuelve el activo ("no encontrado").

export const PARAM_QR = 'activo';
export const CLAVE_QR_PENDIENTE = 'ss_activo_qr';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const enlaceActivo = (origen: string, id: string) => `${origen.replace(/\/+$/, '')}/?${PARAM_QR}=${encodeURIComponent(id)}`;

/** Id del activo en la URL (null si no hay o no es un id válido). */
export function activoDeUrl(search: string): string | null {
  const v = new URLSearchParams(search).get(PARAM_QR);
  return v && UUID.test(v.trim()) ? v.trim().toLowerCase() : null;
}

/** Quita el parámetro del QR de la URL (para no reabrir la ficha al recargar). */
export function urlSinQr(pathname: string, search: string): string {
  const p = new URLSearchParams(search);
  p.delete(PARAM_QR);
  const s = p.toString();
  return pathname + (s ? `?${s}` : '');
}

// Id del QR de esta carga de la página: se lee una sola vez (el estado inicial de React puede calcularse dos veces), se
// guarda para que el inventario abra la ficha y se quita de la URL.
let qrDeEstaCarga: string | null | undefined;
export function qrDeLaCarga(loc: Pick<Location, 'search' | 'pathname'> = window.location, hist: Pick<History, 'replaceState'> = window.history): string | null {
  if (qrDeEstaCarga === undefined) {
    qrDeEstaCarga = activoDeUrl(loc.search);
    if (qrDeEstaCarga) {
      try { sessionStorage.setItem(CLAVE_QR_PENDIENTE, qrDeEstaCarga); } catch { /* sin sessionStorage: solo se abre el inventario */ }
      hist.replaceState({}, '', urlSinQr(loc.pathname, loc.search));
    }
  }
  return qrDeEstaCarga;
}
/** Solo para pruebas. */
export const reiniciarQrDeLaCarga = () => { qrDeEstaCarga = undefined; };

export type DestinoQr = { tipo: 'login' } | { tipo: 'ficha'; id: string } | { tipo: 'no_encontrado' } | { tipo: 'nada' };

/**
 * Qué hace la app con un enlace de QR. Sin sesión: login, sin datos (no se consulta la base). Con sesión: la ficha si la
 * consulta (con la RLS del usuario) devolvió el activo; si no, "no encontrado" (no dice si existe en otra empresa).
 */
export function destinoQr(p: { id: string | null; haySesion: boolean; activoVisible?: boolean | null }): DestinoQr {
  if (!p.id) return { tipo: 'nada' };
  if (!p.haySesion) return { tipo: 'login' };
  if (p.activoVisible === false) return { tipo: 'no_encontrado' };
  return { tipo: 'ficha', id: p.id };
}

/** Imagen del QR (PNG en data URL). Se importa al usarla para no cargar la librería en el arranque. */
export async function qrDataUrl(texto: string, ancho = 320): Promise<string> {
  const QR = await import('qrcode');
  return QR.toDataURL(texto, { errorCorrectionLevel: 'M', margin: 2, width: ancho, color: { dark: '#000000', light: '#ffffff' } });
}

const escapar = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

/** HTML de la etiqueta imprimible (código, nombre, empresa y QR). Sin datos sensibles: el QR solo lleva el enlace. */
export function htmlEtiqueta(p: { codigo: string; nombre: string; empresa: string; qr: string }): string {
  return `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><title>Etiqueta ${escapar(p.codigo)}</title>
<style>@page{size:auto;margin:8mm}body{font-family:Arial,sans-serif;margin:0}
.e{width:62mm;border:1px solid #000;border-radius:3mm;padding:3mm;text-align:center}
.e img{width:44mm;height:44mm}.c{font-size:16pt;font-weight:bold;margin:1mm 0}.n{font-size:9pt}.s{font-size:7pt;color:#333;margin-top:1mm}</style>
</head><body><div class="e"><div class="c">${escapar(p.codigo)}</div><img src="${p.qr}" alt="Código QR del activo ${escapar(p.codigo)}">
<div class="n">${escapar(p.nombre)}</div><div class="s">${escapar(p.empresa)} · Escanea para ver la ficha (requiere iniciar sesión)</div></div>
<script>window.onload=function(){window.print()}</script></body></html>`;
}
